'use client';

import { useEffect, useMemo, useState } from 'react';
import GfArticleCards from '@/components/common/GfArticleCards';
import GfVerticalHero from '@/components/common/GfVerticalHero';
import { getLocalizedContent } from '@/utils/multilingual';
import { getCategoryTitle } from '@/utils/categoryHelpers';
import { t } from '@/i18n/translations';

/**
 * Blog home — "Gallery · Field" design: masthead + filter bar (category chips +
 * country select + search) + rhythm card grid. All filtering is client-side.
 */
export default function GfBlogHome({ posts = [], categories = [], language = 'ru' }) {
    const isRu = language === 'ru';
    const ui = t(language, 'blogUi');
    const [cat, setCat] = useState('all');
    const [country, setCountry] = useState('all');
    const [query, setQuery] = useState('');

    // «Все статьи» со страниц стран и городов ведут на /blog?country=X, а фильтр не включался:
    // читатель попадал в общую ленту из 260 статей (ревью 30.09.2026). Читаем параметры на
    // клиенте, чтобы страница оставалась статической.
    useEffect(() => {
        try {
            const params = new URLSearchParams(window.location.search);
            const c = params.get('country');
            const k = params.get('category');
            if (c && c !== 'null') setCountry(c);
            if (k && k !== 'null') setCat(k);
        } catch { /* нет window.location — остаёмся на «все» */ }
    }, []);

    const catSlug = (c) => c.slug?.current || c.slug;
    const catLabel = (c) => getCategoryTitle(c, language) || catSlug(c) || '';

    // Categories that actually have posts.
    const usedCats = useMemo(() => {
        const counts = new Set();
        posts.forEach((p) => (p.categories || []).forEach((c) => {
            const s = c.slug?.current || c.slug;
            if (s) counts.add(s);
        }));
        return categories.filter((c) => counts.has(catSlug(c)));
    }, [posts, categories]);

    // Countries present across posts.
    const countries = useMemo(() => {
        const map = new Map();
        posts.forEach((p) => (p.countries || []).forEach((c) => {
            const s = c.slug?.current || c.slug;
            if (s && !map.has(s)) map.set(s, { value: s, label: (isRu ? c.nameRu : c.nameEn) || c.name || s });
        }));
        return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
    }, [posts, isRu]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        return posts.filter((p) => {
            if (cat !== 'all' && !(p.categories || []).some((c) => (c.slug?.current || c.slug) === cat)) return false;
            if (country !== 'all') {
                const inCountry = p.isGlobal === true || (p.countries || []).some((c) => (c.slug?.current || c.slug) === country);
                if (!inCountry) return false;
            }
            if (q) {
                const content = getLocalizedContent(p, language);
                const hay = `${content?.title || ''} ${content?.description || ''}`.toLowerCase();
                if (!hay.includes(q)) return false;
            }
            return true;
        });
    }, [posts, cat, country, query, language]);

    const countLabel = ui.articlesCount(filtered.length);
    const activeCatLabel = cat === 'all' ? '' : catLabel(usedCats.find((c) => catSlug(c) === cat) || {});

    return (
        <main className="gf-root">
            <GfVerticalHero background="/images/hero/blog.webp" title={ui.mastTitle} sub={ui.mastSub} />

            <nav className="bl-filter" aria-label={ui.filtersAria}>
                <div className="bl-filter-in">
                    <div className="gf-ops" role="group">
                        <button type="button" className={`all ${cat === 'all' ? 'on' : ''}`} onClick={() => setCat('all')}>{ui.all}</button>
                        {usedCats.map((c) => (
                            <button type="button" key={catSlug(c)} className={cat === catSlug(c) ? 'on' : ''} onClick={() => setCat(catSlug(c))}>{catLabel(c)}</button>
                        ))}
                    </div>
                    <div className="bl-controls">
                        <div className="bl-search">
                            <svg viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" /></svg>
                            <input
                                type="search"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder={ui.searchPlaceholder}
                                aria-label={ui.searchAria}
                            />
                        </div>
                        {countries.length > 1 && (
                            <div className="bl-cosel">
                                <select value={country} onChange={(e) => setCountry(e.target.value)} aria-label={ui.countryAria}>
                                    <option value="all">{ui.allCountries}</option>
                                    {countries.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                                </select>
                            </div>
                        )}
                    </div>
                </div>
            </nav>

            <div className="bl-main">
                <div className="bl-secmark">
                    <span className="no">{ui.journal}</span>
                    <span className="r">{countLabel}</span>
                </div>
                <h2 className="bl-sech2">{cat === 'all'
                    ? (query.trim() ? ui.searchHeading(query.trim()) : ui.latest)
                    : (activeCatLabel || ui.articles)}</h2>
                {filtered.length > 0
                    ? <GfArticleCards posts={filtered} lang={language} limit={filtered.length} variant="bento" />
                    : <p className="gf-lead" style={{ marginTop: 28 }}>{ui.nothingFound}</p>}
            </div>
        </main>
    );
}
